/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useMemo } from 'react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormSection, SearchAutocomplete } from '@/shared/ui/components/form';
import type { SearchAutocompleteOption } from '@/shared/ui/components/form';
import { useAgency } from '@/pages/authenticated/agency-pages/AgenciesContext';
import { isSoloBrokerAgency } from '@/shared/utils/agency-type';
import type { ClientFormData } from './schema';

interface ResponsibleSectionProps {
  form: UseFormReturn<ClientFormData>;
}

/**
 * Who answers for this client — the same field a listing carries — and
 * who brought them in (`assignment.captured_by`, plan 2026-10-28 §11.1).
 *
 * Persists `assignment.primary_broker` keyed by e-mail, the identity every
 * assignment field in the product uses. Hidden for solo-broker agencies,
 * where there is only one member to name; mirrors the listing form's
 * `AssignmentSection`.
 *
 * @param props - The client form instance.
 * @returns The responsible-broker picker, or null for a solo broker.
 */
export const ResponsibleSection: React.FC<ResponsibleSectionProps> = ({ form }) => {
  const { t } = useTranslation('features/clients');
  const agency = useAgency((s) => s.agency);

  const brokerOptions: SearchAutocompleteOption[] = useMemo(() => {
    const members = agency?.members ?? [];
    return members.map((m) => ({
      id: m.email,
      label: `${m.first_name} ${m.last_name}`.trim() || m.email,
      sublabel: m.email,
    }));
  }, [agency?.members]);

  if (isSoloBrokerAgency(agency?.type)) return null;

  return (
    <FormSection title={t('drawer.responsible.title')} variant="quiet">
      <div className="grid grid-cols-1 gap-(--space-3) sm:grid-cols-2">
        <Controller
          name="primaryBroker"
          control={form.control}
          render={({ field }) => (
            <SearchAutocomplete
              label={t('drawer.responsible.label')}
              options={brokerOptions}
              value={field.value || null}
              onChange={(id) => field.onChange(id ?? '')}
              placeholder={t('drawer.responsible.placeholder')}
              emptyLabel={t('drawer.responsible.empty')}
            />
          )}
        />
        <Controller
          name="capturedBy"
          control={form.control}
          render={({ field }) => (
            <SearchAutocomplete
              label={t('drawer.responsible.capturedBy')}
              options={brokerOptions}
              value={field.value || null}
              onChange={(id) => field.onChange(id ?? '')}
              placeholder={t('drawer.responsible.placeholder')}
              emptyLabel={t('drawer.responsible.empty')}
            />
          )}
        />
      </div>
    </FormSection>
  );
};
